import { apiRequest, ApiError } from "@/api/client";
import { useAuthStore } from "@/auth/authStore";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

export type ConferenceSettings = {
  id: string;
  name_en: string;
  name_ar: string;
  edition: string;
  theme_en: string;
  theme_ar: string;
  start_at: string;
  end_at: string;
  timezone: string;
  national_conference_date: string | null;
  venue_name_en: string;
  venue_name_ar: string;
  address_en: string;
  address_ar: string;
  map_url: string | null;
  abstract_deadline: string;
  paper_deadline: string;
  general_submission_deadline: string | null;
  contact_email: string;
  phone_primary: string;
  phone_secondary: string | null;
  director_name_en: string;
  director_name_ar: string;
  registration_open: boolean;
  paper_submission_open: boolean;
  hero_announcement_en: string | null;
  hero_announcement_ar: string | null;
  reminders_enabled?: boolean;
  general_card_title?: string;
  participant_card_title?: string;
  hotel_card_title?: string;
  card_brand_name?: string | null;
  card_letterhead_path?: string | null;
  card_width_mm?: number;
  card_height_mm?: number;
  card_safe_margin_mm?: number;
};

export type ConferenceSession = {
  id: string;
  title_en: string;
  title_ar: string;
  description_en: string | null;
  description_ar: string | null;
  session_type: string;
  speaker_en: string | null;
  speaker_ar: string | null;
  starts_at: string;
  ends_at: string;
  venue_en: string | null;
  venue_ar: string | null;
  stream_url: string | null;
  stream_provider: string | null;
  status: string;
  display_order: number;
};

export type ConferenceRegistration = {
  id: string;
  user_id: string;
  registration_code: string;
  full_name: string;
  full_name_ar: string | null;
  phone: string;
  country: string;
  city: string | null;
  institution: string;
  title_or_role: string;
  preferred_language: string;
  attendance_mode: string;
  organization: string | null;
  checked_in: boolean;
  created_at: string;
  email?: string | null;
  has_portrait?: boolean;
  has_hotel_reservation?: boolean;
  card_type?: "participant" | "hotel";
  is_reviewer?: boolean;
};

export type ConferencePaper = {
  id: string;
  registration_id: string | null;
  user_id: string | null;
  title: string;
  title_alt: string | null;
  language: string;
  abstract: string | null;
  keywords: string | null;
  authors: string;
  affiliation: string;
  original_filename: string | null;
  status: string;
  admin_note: string | null;
  decision?: string | null;
  decision_message?: string | null;
  decided_at?: string | null;
  decided_by_admin_id?: string | null;
  current_revision_number?: number;
  conference_number?: number | null;
  conference_year?: number | null;
  is_public?: boolean | null;
  is_external_archive?: boolean | null;
  has_pdf?: boolean;
  doi?: string | null;
  isbn?: string | null;
  acknowledgements?: string | null;
  license?: string | null;
  published_at?: string | null;
  created_at: string;
};

export type ConferencePaperReviewerFeedback = {
  assignment_id: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_email?: string | null;
  reviewer_institution: string;
  assignment_status: string;
  assigned_at: string;
  due_at?: string | null;
  review_status?: string | null;
  recommendation?: string | null;
  comments?: string | null;
  submitted_at?: string | null;
  is_pending: boolean;
};

export type ConferencePaperDecisionEvent = {
  id: string;
  paper_id: string;
  decision: string;
  previous_decision?: string | null;
  previous_status?: string | null;
  resulting_status: string;
  decision_message?: string | null;
  admin_id?: string | null;
  is_update: boolean;
  created_at: string;
};

export type ConferencePaperRevision = {
  id: string;
  paper_id: string;
  revision_number: number;
  original_filename?: string | null;
  revision_notes?: string | null;
  submitted_by_user_id?: string | null;
  submitted_at: string;
  is_latest?: boolean;
};

export type ConferencePaperReviewDecision = {
  paper: ConferencePaper;
  reviewers: ConferencePaperReviewerFeedback[];
  pending_reviews: number;
  completed_reviews: number;
  decision_history: ConferencePaperDecisionEvent[];
  revisions?: ConferencePaperRevision[];
};

export async function fetchSettings(): Promise<ConferenceSettings> {
  const res = await apiRequest<{ data: ConferenceSettings }>("/v1/conference/settings", {
    anonymous: true,
  });
  return res.data;
}

export async function fetchSessions(): Promise<ConferenceSession[]> {
  const res = await apiRequest<{ data: ConferenceSession[] }>("/v1/conference/sessions", {
    anonymous: true,
  });
  return res.data;
}

export async function fetchCfpTopics(): Promise<{ en: unknown; ar: unknown; fr?: unknown }> {
  const res = await apiRequest<{ data: { en: unknown; ar: unknown; fr?: unknown } }>(
    "/v1/conference/cfp-topics",
    { anonymous: true },
  );
  return res.data;
}

export async function registerParticipant(body: Record<string, unknown>) {
  return apiRequest<{
    data: {
      access_token: string;
      refresh_token?: string;
      registration: ConferenceRegistration;
      user?: { id: string; email: string; full_name: string };
    };
  }>("/v1/conference/register", {
    method: "POST",
    body,
    anonymous: true,
  });
}

export async function fetchMyRegistration(): Promise<ConferenceRegistration> {
  const res = await apiRequest<{ data: ConferenceRegistration }>("/v1/conference/me");
  return res.data;
}

export type ConferenceParticipantContext = {
  has_registration: boolean;
  is_reviewer: boolean;
  reviewer_kind: "registered" | "external" | null;
  registration: ConferenceRegistration | null;
};

export async function fetchMyConferenceContext(): Promise<ConferenceParticipantContext> {
  const res = await apiRequest<{ data: ConferenceParticipantContext }>("/v1/conference/me/context");
  return res.data;
}

export async function changePassword(body: {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
  refresh_token?: string;
}) {
  const res = await apiRequest<{ data: import("@/auth/sessionTypes").TokenPayload & { message?: string } }>(
    "/v1/auth/change-password",
    { method: "POST", body },
  );
  return res.data;
}

export async function fetchMyPapers(): Promise<ConferencePaper[]> {
  const res = await apiRequest<{ data: ConferencePaper[] }>("/v1/conference/papers/me");
  return res.data;
}

export type ConferencePaperPublic = {
  id: string;
  title: string;
  title_alt: string | null;
  authors: string;
  affiliation: string;
  language: string;
  keywords: string | null;
  conference_number: number;
  conference_year: number;
  conference_title_en: string;
  conference_title_ar: string;
  is_external_archive: boolean;
  has_pdf: boolean;
  source_url: string | null;
  doi: string | null;
  license: string | null;
  published_at: string | null;
  created_at: string;
};

export type ConferencePaperPublicDetail = ConferencePaperPublic & {
  abstract: string | null;
};

export type ConferenceEdition = {
  number: number;
  year: number;
  title_en: string;
  title_ar: string;
  label_en: string;
  label_ar: string;
  paper_count: number;
};

export type PublicPapersQuery = {
  search?: string;
  language?: string;
  conference?: number[];
  limit?: number;
  offset?: number;
};

export async function fetchPublicPapers(query: PublicPapersQuery = {}) {
  const params = new URLSearchParams();
  if (query.search?.trim()) params.set("search", query.search.trim());
  if (query.language) params.set("language", query.language);
  for (const n of query.conference ?? []) {
    params.append("conference", String(n));
  }
  params.set("limit", String(query.limit ?? 20));
  params.set("offset", String(query.offset ?? 0));
  const res = await apiRequest<{
    data: ConferencePaperPublic[];
    meta: { limit: number; offset: number; count: number; total: number };
  }>(`/v1/conference/papers/public?${params.toString()}`, { anonymous: true });
  return res;
}

export async function fetchPaperEditions() {
  return apiRequest<{
    data: ConferenceEdition[];
    meta: { limit: number; offset: number; count: number; total: number };
  }>("/v1/conference/papers/editions", { anonymous: true });
}

export async function fetchPublicPaper(paperId: string): Promise<ConferencePaperPublicDetail> {
  const res = await apiRequest<{ data: ConferencePaperPublicDetail }>(
    `/v1/conference/papers/public/${paperId}`,
    { anonymous: true },
  );
  return res.data;
}

export function publicPaperPdfUrl(paperId: string, download = false) {
  const q = download ? "?download=true" : "";
  return `/api/v1/conference/papers/public/${paperId}/pdf${q}`;
}

export async function verifyRegistration(code: string) {
  return apiRequest<{
    data: {
      registration_code: string;
      full_name: string;
      conference_name: string;
      status: string;
    };
  }>(`/v1/conference/verify/${encodeURIComponent(code)}`, { anonymous: true });
}

export async function downloadBadgeBlob(): Promise<Blob> {
  throw new ApiError({
    status: 403,
    code: "card_admin_only",
    message: "Conference cards are issued physically at the venue and cannot be downloaded by participants.",
  });
}

export async function adminResendRegistration(id: string) {
  return apiRequest(`/v1/admin/conference/registrations/${id}/resend-registration`, { method: "POST" });
}

/** @deprecated Use adminResendRegistration — no longer attaches a card PDF. */
export async function adminResendBadge(id: string) {
  return adminResendRegistration(id);
}

export async function adminDownloadCard(
  id: string,
  inline = false,
  format: "pdf" | "png" = "pdf",
): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const params = new URLSearchParams({ format });
  if (inline) params.set("inline", "true");
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/registrations/${id}/card?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError({ status: res.status, code: "card_error", message: "Card download failed" });
  return res.blob();
}

/** @deprecated Use adminDownloadCard */
export async function adminDownloadBadge(id: string): Promise<Blob> {
  return adminDownloadCard(id);
}

export async function adminDownloadGeneralCards(
  copies = 1,
  inline = false,
  format: "pdf" | "png" = "pdf",
): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const params = new URLSearchParams({ copies: String(copies), format });
  if (inline) params.set("inline", "true");
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/cards/general?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError({ status: res.status, code: "card_error", message: "General card download failed" });
  }
  return res.blob();
}

export async function adminPreviewCard(id: string): Promise<Blob> {
  // PNG is clearer for on-screen preview; PDF remains the print download.
  return adminDownloadCard(id, true, "png");
}

export async function submitPaper(form: FormData) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/v1/conference/papers`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: json?.code || "submit_failed",
      message: json?.message || "Paper submission failed",
      detail: json,
    });
  }
  return json as { data: ConferencePaper };
}

export async function submitPaperRevision(
  paperId: string,
  form: FormData,
): Promise<{ paper: ConferencePaper; revision: ConferencePaperRevision }> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/v1/conference/papers/${paperId}/revisions`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = json?.detail;
    throw new ApiError({
      status: res.status,
      code: detail?.code || json?.code || "revision_failed",
      message: detail?.message || json?.message || "Revision submission failed",
      detail: json,
    });
  }
  return json.data as { paper: ConferencePaper; revision: ConferencePaperRevision };
}

export async function adminStats() {
  const res = await apiRequest<{
    data: {
      total_registrations: number;
      papers_submitted: number;
      accepted_papers: number;
      upcoming_sessions: number;
      checked_in: number;
    };
  }>("/v1/admin/conference/dashboard-stats");
  return res.data;
}

export async function adminRegistrations(q?: string) {
  const qs = q?.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
  const res = await apiRequest<{ data: ConferenceRegistration[] }>(
    `/v1/admin/conference/registrations${qs}`,
  );
  return res.data;
}

export type ConferenceHotelReservationInput = {
  hotel_name: string;
  hotel_address: string;
  check_in_date: string;
  check_out_date: string;
  booking_reference: string;
  room_type: string;
  room_number?: string | null;
  contact_phone: string;
  notes?: string | null;
  maps_url?: string | null;
};

export type ConferenceHotelReservation = ConferenceHotelReservationInput & {
  id: string;
  registration_id: string;
  user_id: string;
  status: string;
  last_sent_at: string | null;
  last_error: string | null;
  preferred_language_at_send: string | null;
  created_at: string;
  updated_at: string;
};

export type ConferenceHotelReservationSend = {
  id: string;
  reservation_id: string;
  registration_id: string;
  email: string;
  preferred_language: string | null;
  status: string;
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
};

export type ConferenceHotelReservationBundle = {
  registration: {
    id: string;
    full_name: string;
    email: string;
    registration_code: string;
    preferred_language: string;
    phone: string;
  };
  reservation: ConferenceHotelReservation | null;
  sends: ConferenceHotelReservationSend[];
};

export type ConferenceHotelEmailPreview = {
  to_email: string;
  preferred_language: string;
  subject: string;
  html: string;
  text: string;
};

export async function adminGetHotelReservation(registrationId: string) {
  const res = await apiRequest<{ data: ConferenceHotelReservationBundle }>(
    `/v1/admin/conference/registrations/${registrationId}/hotel-reservation`,
  );
  return res.data;
}

export async function adminUpsertHotelReservation(
  registrationId: string,
  body: ConferenceHotelReservationInput,
) {
  const res = await apiRequest<{ data: ConferenceHotelReservationBundle }>(
    `/v1/admin/conference/registrations/${registrationId}/hotel-reservation`,
    { method: "PUT", body },
  );
  return res.data;
}

export async function adminPreviewHotelReservation(
  registrationId: string,
  body: ConferenceHotelReservationInput,
) {
  const res = await apiRequest<{ data: ConferenceHotelEmailPreview }>(
    `/v1/admin/conference/registrations/${registrationId}/hotel-reservation/preview`,
    { method: "POST", body },
  );
  return res.data;
}

export async function adminSendHotelReservation(
  registrationId: string,
  body: ConferenceHotelReservationInput,
) {
  const res = await apiRequest<{ data: ConferenceHotelReservationBundle }>(
    `/v1/admin/conference/registrations/${registrationId}/hotel-reservation/send`,
    { method: "POST", body },
  );
  return res.data;
}

export async function adminPapers() {
  const res = await apiRequest<{ data: ConferencePaper[] }>("/v1/admin/conference/papers");
  return res.data;
}

export async function adminUpdatePaper(id: string, body: { status: string; admin_note?: string }) {
  return apiRequest<{ data: ConferencePaper }>(`/v1/admin/conference/papers/${id}`, {
    method: "PATCH",
    body,
  });
}

export async function adminGetPaper(id: string) {
  const res = await apiRequest<{ data: ConferencePaper }>(`/v1/admin/conference/papers/${id}`);
  return res.data;
}

export async function adminGetPaperReviewDecision(paperId: string) {
  const res = await apiRequest<{ data: ConferencePaperReviewDecision }>(
    `/v1/admin/conference/papers/${paperId}/review-decision`,
  );
  return res.data;
}

export async function adminSetPaperDecision(
  paperId: string,
  body: { decision: string; decision_message?: string | null; force_update?: boolean },
) {
  const res = await apiRequest<{ data: ConferencePaper }>(
    `/v1/admin/conference/papers/${paperId}/decision`,
    { method: "POST", body },
  );
  return res.data;
}

export async function adminDownloadPaperUrl(id: string) {
  const res = await apiRequest<{ data: { download_url: string; filename: string | null } }>(
    `/v1/admin/conference/papers/${id}/download`,
  );
  return res.data;
}

export async function adminDownloadRevisionUrl(paperId: string, revisionId: string) {
  const res = await apiRequest<{
    data: { download_url: string; filename: string | null; revision_number: number };
  }>(`/v1/admin/conference/papers/${paperId}/revisions/${revisionId}/download`);
  return res.data;
}

export async function adminOpenPaperPdf(id: string, download = false): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const qs = download ? "?download=true" : "";
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/papers/${id}/pdf${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "pdf_unavailable",
      message: json?.detail?.message || json?.message || "PDF not available",
      detail: json,
    });
  }
  return res.blob();
}

export async function adminDeletePaper(id: string) {
  return apiRequest<{ data: { status: string; message: string } }>(
    `/v1/admin/conference/papers/${id}`,
    { method: "DELETE" },
  );
}

export async function adminPublishPreview(id: string, form: FormData): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/papers/${id}/publish-preview`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "preview_failed",
      message: json?.detail?.message || json?.message || "Preview failed",
      detail: json,
    });
  }
  return res.blob();
}

export async function adminPublishPaper(id: string, form: FormData) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/papers/${id}/publish`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "publish_failed",
      message: json?.detail?.message || json?.message || "Publish failed",
      detail: json,
    });
  }
  return (json as { data: ConferencePaper }).data;
}

export async function adminUpdateSettings(body: Partial<ConferenceSettings>) {
  return apiRequest<{ data: ConferenceSettings }>("/v1/admin/conference/settings", {
    method: "PATCH",
    body,
  });
}

export type ConferenceReminderStatus = {
  reminders_enabled: boolean;
  timezone: string;
  conference_start_at: string;
  days_remaining: number;
  smtp_enabled: boolean;
  eligible_recipients: number;
  today_occurrence: {
    window: string;
    period_key: string;
    scheduled_for: string;
    days_remaining: number;
  } | null;
  next_scheduled: {
    window: string;
    period_key: string;
    scheduled_for: string;
    days_remaining: number;
    timezone: string;
  } | null;
  totals: { sent: number; failed: number };
  recent: Array<{
    id: string;
    email: string;
    window: string;
    period_key: string;
    scheduled_for: string | null;
    days_remaining: number | null;
    preferred_language: string | null;
    status: string;
    error_message: string | null;
    sent_at: string | null;
    created_at: string | null;
  }>;
};

export async function adminReminderStatus() {
  const res = await apiRequest<{ data: ConferenceReminderStatus }>(
    "/v1/admin/conference/reminders",
  );
  return res.data;
}

export async function adminRunReminders() {
  const res = await apiRequest<{ data: Record<string, unknown> }>(
    "/v1/admin/conference/reminders/run",
    { method: "POST" },
  );
  return res.data;
}

export async function adminTestReminder(body: { email: string; days_remaining?: number }) {
  const res = await apiRequest<{ data: Record<string, unknown> }>(
    "/v1/admin/conference/reminders/test",
    { method: "POST", body },
  );
  return res.data;
}

export type ConferenceAdminMessage = {
  id: string;
  admin_user_id: string;
  recipient_email: string;
  subject: string;
  body: string;
  status: string;
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
};

export async function adminSendMessage(body: { email: string; subject: string; body: string }) {
  const res = await apiRequest<{ data: ConferenceAdminMessage }>(
    "/v1/admin/conference/messages/send",
    { method: "POST", body },
  );
  return res.data;
}

export async function adminListMessages(params?: { limit?: number; offset?: number }) {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await apiRequest<{ data: ConferenceAdminMessage[] }>(
    `/v1/admin/conference/messages${suffix}`,
  );
  return res.data;
}

export async function adminMarkLive(id: string) {
  return apiRequest<{ data: ConferenceSession }>(`/v1/admin/conference/sessions/${id}/mark-live`, {
    method: "POST",
  });
}

export async function adminMarkEnded(id: string) {
  return apiRequest<{ data: ConferenceSession }>(`/v1/admin/conference/sessions/${id}/mark-ended`, {
    method: "POST",
  });
}

export async function adminCheckIn(id: string) {
  return apiRequest(`/v1/admin/conference/registrations/${id}/check-in`, { method: "POST" });
}

export type ConferenceReviewer = {
  id: string;
  registration_id: string | null;
  user_id: string;
  is_active: boolean;
  assigned_at: string;
  assigned_by_admin_id?: string | null;
  removed_at?: string | null;
  removed_by_admin_id?: string | null;
  full_name: string;
  email?: string | null;
  institution?: string | null;
  registration_code?: string | null;
  reviewer_kind: "registered" | "external";
  phone?: string | null;
  country?: string | null;
  title_or_role?: string | null;
  expertise_keywords?: string | null;
  preferred_language?: string | null;
  must_change_password?: boolean;
};

export type ConferenceExternalReviewerCreateInput = {
  full_name: string;
  email: string;
  institution?: string | null;
  phone?: string | null;
  country?: string | null;
  title_or_role?: string | null;
  expertise_keywords?: string | null;
  preferred_language?: string | null;
};

export async function adminMakeReviewer(id: string) {
  const res = await apiRequest<{ data: ConferenceRegistration }>(
    `/v1/admin/conference/registrations/${id}/make-reviewer`,
    { method: "POST" },
  );
  return res.data;
}

export async function adminRemoveReviewer(id: string) {
  const res = await apiRequest<{ data: ConferenceRegistration }>(
    `/v1/admin/conference/registrations/${id}/remove-reviewer`,
    { method: "POST" },
  );
  return res.data;
}

export async function adminCreateExternalReviewer(body: ConferenceExternalReviewerCreateInput) {
  const res = await apiRequest<{
    data: {
      reviewer: ConferenceReviewer;
      created_user: boolean;
      email_kind: string;
      message: string;
    };
  }>("/v1/admin/conference/reviewers", { method: "POST", body });
  return res.data;
}

export async function adminResendTemporaryPassword(reviewerId: string) {
  const res = await apiRequest<{ data: ConferenceReviewer }>(
    `/v1/admin/conference/reviewers/${reviewerId}/resend-temporary-password`,
    { method: "POST" },
  );
  return res.data;
}

export async function adminRemoveReviewerById(reviewerId: string) {
  const res = await apiRequest<{ data: ConferenceReviewer }>(
    `/v1/admin/conference/reviewers/${reviewerId}/remove`,
    { method: "POST" },
  );
  return res.data;
}

export async function adminListReviewers(q?: string, includeInactive = false) {
  const params = new URLSearchParams();
  if (q?.trim()) params.set("q", q.trim());
  if (includeInactive) params.set("include_inactive", "true");
  const qs = params.toString() ? `?${params}` : "";
  const res = await apiRequest<{ data: ConferenceReviewer[] }>(
    `/v1/admin/conference/reviewers${qs}`,
  );
  return res.data;
}

export type ConferenceReview = {
  id: string;
  assignment_id: string;
  reviewer_id: string;
  paper_id: string;
  user_id: string;
  comments: string;
  recommendation?: string | null;
  status: string;
  submitted_at?: string | null;
  reopened_at?: string | null;
  reopened_by_admin_id?: string | null;
  created_at: string;
  updated_at: string;
};

export type ConferenceReviewAssignment = {
  id: string;
  reviewer_id: string;
  paper_id: string;
  user_id: string;
  registration_id: string;
  status: string;
  assigned_at: string;
  assigned_by_admin_id?: string | null;
  due_at?: string | null;
  cancelled_at?: string | null;
  cancelled_by_admin_id?: string | null;
  assignment_email_sent_at?: string | null;
  assignment_email_sent?: boolean;
  latest_reminder_kind?: string | null;
  latest_reminder_sent_at?: string | null;
  is_overdue?: boolean;
  title: string;
  authors: string;
  language: string;
  keywords?: string | null;
  abstract?: string | null;
  conference_number?: number | null;
  conference_year?: number | null;
  paper_status: string;
  has_manuscript?: boolean;
  review?: ConferenceReview | null;
};

export type ConferenceReviewerDetail = {
  reviewer: ConferenceReviewer;
  assignments: ConferenceReviewAssignment[];
};

export type ConferenceAdminReview = {
  assignment: ConferenceReviewAssignment;
  reviewer_name: string;
  reviewer_email?: string | null;
  reviewer_institution: string;
  review?: ConferenceReview | null;
};

export async function fetchMyReviewAssignments() {
  const res = await apiRequest<{ data: ConferenceReviewAssignment[] }>(
    "/v1/conference/reviews/assignments",
  );
  return res.data;
}

export async function fetchMyReviewAssignment(assignmentId: string) {
  const res = await apiRequest<{ data: ConferenceReviewAssignment }>(
    `/v1/conference/reviews/assignments/${assignmentId}`,
  );
  return res.data;
}

export async function fetchReviewManuscriptUrl(assignmentId: string) {
  const res = await apiRequest<{ data: { download_url: string; filename: string | null } }>(
    `/v1/conference/reviews/assignments/${assignmentId}/manuscript`,
  );
  return res.data;
}

export async function saveReviewDraft(
  assignmentId: string,
  body: { comments: string; recommendation?: string | null },
) {
  const res = await apiRequest<{ data: ConferenceReview }>(
    `/v1/conference/reviews/assignments/${assignmentId}/draft`,
    { method: "PUT", body },
  );
  return res.data;
}

export async function submitReview(
  assignmentId: string,
  body: { comments: string; recommendation: string },
) {
  const res = await apiRequest<{ data: ConferenceReview }>(
    `/v1/conference/reviews/assignments/${assignmentId}/submit`,
    { method: "POST", body },
  );
  return res.data;
}

export async function adminGetReviewer(reviewerId: string, includeCancelled = false) {
  const qs = includeCancelled ? "?include_cancelled=true" : "";
  const res = await apiRequest<{ data: ConferenceReviewerDetail }>(
    `/v1/admin/conference/reviewers/${reviewerId}${qs}`,
  );
  return res.data;
}

export async function adminSearchAssignablePapers(
  reviewerId: string,
  opts: {
    q?: string;
    language?: string;
    conference_number?: number | null;
    keywords?: string;
  } = {},
) {
  const params = new URLSearchParams();
  if (opts.q?.trim()) params.set("q", opts.q.trim());
  if (opts.language) params.set("language", opts.language);
  if (opts.conference_number != null && opts.conference_number !== undefined) {
    params.set("conference_number", String(opts.conference_number));
  }
  if (opts.keywords?.trim()) params.set("keywords", opts.keywords.trim());
  const qs = params.toString() ? `?${params}` : "";
  const res = await apiRequest<{ data: ConferencePaper[] }>(
    `/v1/admin/conference/reviewers/${reviewerId}/assignable-papers${qs}`,
  );
  return res.data;
}

export async function adminAssignPapers(
  reviewerId: string,
  body: { paper_ids: string[]; due_at?: string | null },
) {
  const res = await apiRequest<{ data: ConferenceReviewAssignment[] }>(
    `/v1/admin/conference/reviewers/${reviewerId}/assignments`,
    { method: "POST", body },
  );
  return res.data;
}

export async function adminCancelReviewAssignment(assignmentId: string) {
  const res = await apiRequest<{ data: ConferenceReviewAssignment }>(
    `/v1/admin/conference/review-assignments/${assignmentId}/cancel`,
    { method: "POST" },
  );
  return res.data;
}

export async function adminResendAssignmentEmail(assignmentId: string) {
  return apiRequest<{ data: { status: string; message: string } }>(
    `/v1/admin/conference/review-assignments/${assignmentId}/resend-assignment-email`,
    { method: "POST" },
  );
}

export async function adminGetAssignmentReview(assignmentId: string) {
  const res = await apiRequest<{ data: ConferenceAdminReview }>(
    `/v1/admin/conference/review-assignments/${assignmentId}/review`,
  );
  return res.data;
}

export async function adminReopenAssignmentReview(assignmentId: string) {
  const res = await apiRequest<{ data: ConferenceReview }>(
    `/v1/admin/conference/review-assignments/${assignmentId}/reopen`,
    { method: "POST" },
  );
  return res.data;
}

export async function adminUpdateSession(id: string, body: Record<string, unknown>) {
  return apiRequest<{ data: ConferenceSession }>(`/v1/admin/conference/sessions/${id}`, {
    method: "PATCH",
    body,
  });
}

export async function adminCreateSession(body: Record<string, unknown>) {
  return apiRequest<{ data: ConferenceSession }>("/v1/admin/conference/sessions", {
    method: "POST",
    body,
  });
}

export async function adminDeleteSession(id: string) {
  return apiRequest(`/v1/admin/conference/sessions/${id}`, { method: "DELETE" });
}


export type ConferenceCorrespondenceRecipient = {
  user_id: string;
  email: string;
  name: string;
  recipient_type: "participant" | "registered_reviewer" | "external_reviewer" | string;
  preferred_language?: string | null;
  title_or_role?: string | null;
  suggested_honorific?: string | null;
};

export type ConferenceCorrespondenceExternalRecipient = {
  email: string;
  name?: string | null;
};

export type ConferenceCorrespondenceCompose = {
  recipient_user_ids: string[];
  external_recipients?: ConferenceCorrespondenceExternalRecipient[];
  title: string;
  body: string;
  body_html?: string | null;
  addressee: string;
  prayer_honorific?: string | null;
  location?: string | null;
  greeting?: string | null;
  closing?: string | null;
  reference?: string | null;
  letter_date?: string | null;
  preview_recipient_user_id?: string | null;
};

export type ConferenceCorrespondenceItem = {
  id: string;
  admin_user_id: string;
  recipient_user_id: string | null;
  recipient_email: string;
  recipient_name: string;
  recipient_type: string;
  title: string;
  body: string;
  body_hash: string;
  addressee?: string | null;
  prayer_honorific?: string | null;
  location?: string | null;
  greeting?: string | null;
  closing?: string | null;
  reference: string | null;
  letter_date: string | null;
  pdf_file_key: string | null;
  delivery_status: string;
  delivery_error: string | null;
  created_at: string;
  sent_at: string | null;
};

export type ConferenceCorrespondenceSendResult = {
  items: ConferenceCorrespondenceItem[];
  sent: number;
  failed: number;
  generated: number;
};

export async function adminCorrespondenceRecipients(q?: string, limit = 50) {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  qs.set("limit", String(limit));
  const res = await apiRequest<{ data: ConferenceCorrespondenceRecipient[] }>(
    `/v1/admin/conference/correspondence/recipients?${qs}`,
  );
  return res.data;
}

async function correspondenceBlob(
  path: string,
  body: ConferenceCorrespondenceCompose,
): Promise<{ blob: Blob; filename: string; contentType: string }> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "correspondence_failed",
      message: json?.detail?.message || json?.message || "Correspondence request failed",
      detail: json,
    });
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const filename = match?.[1] || "correspondence.pdf";
  const contentType = res.headers.get("Content-Type") || "application/pdf";
  return { blob: await res.blob(), filename, contentType };
}

export async function adminCorrespondencePreview(body: ConferenceCorrespondenceCompose) {
  const { blob } = await correspondenceBlob("/v1/admin/conference/correspondence/preview", body);
  return blob;
}

export async function adminGenerateCorrespondence(body: ConferenceCorrespondenceCompose) {
  return correspondenceBlob("/v1/admin/conference/correspondence/generate", body);
}

export async function adminSendCorrespondence(body: ConferenceCorrespondenceCompose) {
  const res = await apiRequest<{ data: ConferenceCorrespondenceSendResult }>(
    "/v1/admin/conference/correspondence/send",
    { method: "POST", body },
  );
  return res.data;
}

export async function adminCorrespondenceHistory(params: {
  q?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  qs.set("limit", String(params.limit ?? 100));
  const res = await apiRequest<{ data: ConferenceCorrespondenceItem[] }>(
    `/v1/admin/conference/correspondence?${qs}`,
  );
  return res.data;
}

export async function adminDownloadCorrespondence(id: string) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(
    `${API_BASE_URL}/v1/admin/conference/correspondence/${id}/download`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "download_failed",
      message: json?.detail?.message || json?.message || "Download failed",
      detail: json,
    });
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/i.exec(cd);
  return { blob: await res.blob(), filename: match?.[1] || "correspondence.pdf" };
}

export async function adminResendCorrespondence(id: string) {
  const res = await apiRequest<{ data: ConferenceCorrespondenceItem }>(
    `/v1/admin/conference/correspondence/${id}/resend`,
    { method: "POST" },
  );
  return res.data;
}

export type ConferenceCertificateRecipientCustomization = {
  user_id: string;
  honorific?: string | null;
  recipient_name?: string | null;
  appreciation?: string | null;
};

export type ConferenceCertificateCompose = {
  recipient_user_ids: string[];
  language?: string | null;
  notes?: string | null;
  preview_recipient_user_id?: string | null;
  customizations?: ConferenceCertificateRecipientCustomization[];
};

export type ConferenceCertificateItem = {
  id: string;
  admin_user_id: string;
  recipient_user_id: string | null;
  recipient_email: string;
  recipient_name: string;
  recipient_type: string;
  language: string;
  template_key: string;
  notes: string | null;
  body_hash: string;
  pdf_file_key: string | null;
  delivery_status: string;
  delivery_error: string | null;
  created_at: string;
  sent_at: string | null;
};

export type ConferenceCertificateSendResult = {
  items: ConferenceCertificateItem[];
  sent: number;
  failed: number;
  generated: number;
};

export async function adminCertificateRecipients(q?: string, limit = 50) {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  qs.set("limit", String(limit));
  const res = await apiRequest<{ data: ConferenceCorrespondenceRecipient[] }>(
    `/v1/admin/conference/certificates/recipients?${qs}`,
  );
  return res.data;
}

async function certificateBlob(
  path: string,
  body: ConferenceCertificateCompose,
): Promise<{ blob: Blob; filename: string; contentType: string }> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "certificate_failed",
      message: json?.detail?.message || json?.message || "Certificate request failed",
      detail: json,
    });
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const filename = match?.[1] || "certificate.pdf";
  const contentType = res.headers.get("Content-Type") || "application/pdf";
  return { blob: await res.blob(), filename, contentType };
}

export async function adminCertificatePreview(body: ConferenceCertificateCompose) {
  const { blob } = await certificateBlob("/v1/admin/conference/certificates/preview", body);
  return blob;
}

export async function adminSendCertificates(body: ConferenceCertificateCompose) {
  const res = await apiRequest<{ data: ConferenceCertificateSendResult }>(
    "/v1/admin/conference/certificates/send",
    { method: "POST", body },
  );
  return res.data;
}

export async function adminCertificateHistory(params: {
  q?: string;
  status?: string;
  language?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.language) qs.set("language", params.language);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  qs.set("limit", String(params.limit ?? 100));
  const res = await apiRequest<{ data: ConferenceCertificateItem[] }>(
    `/v1/admin/conference/certificates?${qs}`,
  );
  return res.data;
}

export async function adminDownloadCertificate(id: string) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(
    `${API_BASE_URL}/v1/admin/conference/certificates/${id}/download`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "download_failed",
      message: json?.detail?.message || json?.message || "Download failed",
      detail: json,
    });
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^";]+)"?/i.exec(cd);
  return { blob: await res.blob(), filename: match?.[1] || "certificate.pdf" };
}

export async function adminResendCertificate(id: string) {
  const res = await apiRequest<{ data: ConferenceCertificateItem }>(
    `/v1/admin/conference/certificates/${id}/resend`,
    { method: "POST" },
  );
  return res.data;
}

export type ConferencePastEvent = {
  id: string;
  slug: string;
  title_en: string;
  title_ar: string;
  title_fr: string;
  year: number | null;
  event_date: string | null;
  description_en: string | null;
  description_ar: string | null;
  description_fr: string | null;
  has_pdf: boolean;
  is_published: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
};

export async function fetchPastEvents() {
  const res = await apiRequest<{ data: ConferencePastEvent[] }>("/v1/conference/past-events", {
    anonymous: true,
  });
  return res.data;
}

export async function fetchPastEvent(idOrSlug: string): Promise<ConferencePastEvent> {
  const res = await apiRequest<{ data: ConferencePastEvent }>(
    `/v1/conference/past-events/${encodeURIComponent(idOrSlug)}`,
    { anonymous: true },
  );
  return res.data;
}

export function pastEventPdfUrl(idOrSlug: string, download = false) {
  const q = download ? "?download=true" : "";
  return `/api/v1/conference/past-events/${encodeURIComponent(idOrSlug)}/pdf${q}`;
}

export async function adminListPastEvents() {
  const res = await apiRequest<{ data: ConferencePastEvent[] }>("/v1/admin/conference/past-events");
  return res.data;
}

export async function adminCreatePastEvent(form: FormData) {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/past-events`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "create_failed",
      message: json?.detail?.message || json?.message || "Create failed",
      detail: json,
    });
  }
  return (json as { data: ConferencePastEvent }).data;
}

export async function adminUpdatePastEvent(
  id: string,
  body: Partial<{
    slug: string;
    title_en: string;
    title_ar: string;
    title_fr: string;
    year: number | null;
    event_date: string | null;
    description_en: string | null;
    description_ar: string | null;
    description_fr: string | null;
    is_published: boolean;
    display_order: number;
  }>,
) {
  const res = await apiRequest<{ data: ConferencePastEvent }>(
    `/v1/admin/conference/past-events/${id}`,
    { method: "PATCH", body },
  );
  return res.data;
}

export async function adminUploadPastEventPdf(id: string, file: File) {
  const form = new FormData();
  form.append("pdf", file);
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${API_BASE_URL}/v1/admin/conference/past-events/${id}/pdf`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError({
      status: res.status,
      code: json?.detail?.code || json?.code || "upload_failed",
      message: json?.detail?.message || json?.message || "Upload failed",
      detail: json,
    });
  }
  return (json as { data: ConferencePastEvent }).data;
}

export async function adminDeletePastEvent(id: string) {
  return apiRequest(`/v1/admin/conference/past-events/${id}`, { method: "DELETE" });
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export { ApiError };
